import React, {useEffect, useState} from "react";
import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import updateLocale from 'dayjs/plugin/updateLocale';
import { formatDateRange } from "../formatters";
import { es } from "date-fns/locale";
import "dayjs/locale/es";
import {WeekFilterDateRangeProps} from "../../../types/project-progress/types";
import {
    FIRST_DAY_OF_WEEK, getDateRange,
    getStartOfWeekByNumber,
    getWeekNumber,
    LAST_DAY_OF_WEEK,
    toFirstDayOfWeek
} from "../../../utils/date-utils";

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(updateLocale);
dayjs.updateLocale('es', {
  weekStart: 1,
});

const esWeekStart = {
  ...es,
  options: {
    ...(es.options || {}),
    weekStartsOn: 4 as 4,
  },
};

interface WeekFilterProps {
  weekFilterDateRangeProps: WeekFilterDateRangeProps;
  onRangeChange?: (startDate: Dayjs, endDate: Dayjs, dateRange: string) => void;
}

type WeekOption = {
    id: number;
    name: string;
}

const WeekFilter: React.FC<WeekFilterProps> = ({
  weekFilterDateRangeProps, onRangeChange,
}) => {
  const getWeekCount = (): number => {
    const year = weekFilterDateRangeProps.endDate.year();
    
    // Obtener el primer jueves del año
    const jan1 = dayjs(`${year}-01-01`);
    const firstThursday = toFirstDayOfWeek(jan1);
    
    // Verificar que el primer jueves pertenezca al año actual
    // Si no, tomar el siguiente jueves
    const actualFirstThursday = firstThursday.year() === year 
      ? firstThursday 
      : firstThursday.add(7, 'day');
    
    // Calcular cuántas semanas completas hay desde el primer jueves hasta fin de año
    const dec31 = dayjs(`${year}-12-31`);
    const daysDifference = dec31.diff(actualFirstThursday, 'day');
    const weeks = Math.floor(daysDifference / 7) + 1;
    
    return weeks;
  }
  const weekOptions: WeekOption[] = Array.from({ length: getWeekCount() }, (_, i) => ({
    id: i + 1,
    name: `Semana ${i + 1}`,
  }));
  const findCurrentWeekOption = (): WeekOption | null => {
      return weekOptions.find(({id}) => id === selectedWeek) ?? null
  }
  const [selectedWeek, setSelectedWeek] = useState<number>(() => getWeekNumber(weekFilterDateRangeProps.endDate));
  const theme = useTheme();
  const [currentWeekOption, setCurrentWeekOption] = useState<WeekOption | null>(() => findCurrentWeekOption());
  const [selectedDate, setSelectedDate] = useState<Dayjs>(weekFilterDateRangeProps.startDate);

  const getDateRangeFromProps = (): string => getDateRange(weekFilterDateRangeProps.startDate, weekFilterDateRangeProps.endDate);

  const onSelectWeek = (_event: any, weekOption: WeekOption | null) => {
    if(!weekOption){
      return;
    }
    const weekNumber = weekOption.id;
    const year = weekFilterDateRangeProps.endDate.year();
    const date = getStartOfWeekByNumber(year, weekNumber);
    setCurrentWeekOption(weekOption);
    setSelectedWeek(weekNumber);
    setSelectedDate(date);
    updateRange(date)
  }

  const onChangeRange = (date: Dayjs | null) => {
    if(!date){
      return;
    }
    const dateAsdayjs = dayjs(date);
    const weekNumber = getWeekNumber(dateAsdayjs);
    const weekOption: WeekOption | null = weekOptions.find(({id}) => id === weekNumber) ?? null;
    setCurrentWeekOption(weekOption);
    setSelectedWeek(weekNumber);
    setSelectedDate(dateAsdayjs);
    updateRange(dateAsdayjs);
  }

  const updateRange = (date: Dayjs)=> {
      const startDate = toFirstDayOfWeek(date);
      // Build end date from components — DST-safe (no millisecond arithmetic)
      const endDateRaw = new Date(startDate.year(), startDate.month(), startDate.date() + 6);
      const endDate = dayjs(endDateRaw).endOf("day");
      onRangeChange?.(startDate, endDate, getDateRange(startDate, endDate));
  }

   useEffect(() => {
    setSelectedWeek(getWeekNumber(weekFilterDateRangeProps.endDate));
    setSelectedDate(weekFilterDateRangeProps.startDate);
   }, [weekFilterDateRangeProps.endDate]);
    useEffect(() => {
        setCurrentWeekOption(findCurrentWeekOption());
    }, [selectedWeek]);
  return (
    <>
      <Grid
        container
        direction="column"
        spacing={2}
        sx={{
          background: theme.palette.secondary.light,
          borderRadius: "10px",
          padding: "15px 15px",
          marginLeft: "2px",
          maxWidth: "375px",
          minWidth: "375px",
        }}
      >
        <Grid item xs={6}>
          <Autocomplete
            options={weekOptions}
            getOptionLabel={(option) => option.name}
            value={currentWeekOption}
            onChange={onSelectWeek}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccionar Número de Semana"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            )}
            disabled={weekFilterDateRangeProps.disabled}
          />
        </Grid>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esWeekStart}>
          <Grid item xs={6}>
            <DatePicker
              label="Seleccionar Día"
              //@ts-ignore
              value={selectedDate.toDate()}
              onChange={onChangeRange}
              format="yyyy-MM-dd"
              disabled={weekFilterDateRangeProps.disabled}
              slots={{
                textField: (props) => (
                    <TextField
                        {...props}
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                ),
              }}
             slotProps={{
                day: (ownerState) => {
                  const dayAsDateJs = dayjs(ownerState.day);
                  const startOfWeek = toFirstDayOfWeek(selectedDate);
                  const endOfWeek = startOfWeek.endOf("day").add(6, "day");
                  const isSelected = dayAsDateJs.isBetween(startOfWeek, endOfWeek, undefined, '[]');

                  let borderRadius = {};

                 if (isSelected) {
                    if (dayAsDateJs.day() === FIRST_DAY_OF_WEEK) {
                      borderRadius = {
                        borderTopLeftRadius: "50%",
                        borderBottomLeftRadius: "50%",
                      };
                    }
                    if (dayAsDateJs.day() === LAST_DAY_OF_WEEK) {
                      borderRadius = {
                        borderTopRightRadius: "50%",
                        borderBottomRightRadius: "50%",
                      };
                    }
                  }

                  return {
                    sx: isSelected
                      ? {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          borderRadius: 0,
                          ...borderRadius,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                          "&:focus": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }
                      : {},
                  };
                },
              }}
              sx={{
                "& .MuiPickersDay-root": {
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
            />
          </Grid>
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12}>
        <Typography
            variant="subtitle2"
            sx={{ marginTop: 2, fontWeight: "bold" }}
        >
          Semana seleccionada: {} ({formatDateRange(getDateRangeFromProps())})
        </Typography>
      </Grid>
    </>
  );
};

export default WeekFilter;
